import type { ChildProfile, WakeProgram } from './types'
import { sleepNormOf } from './programs'
import { ageTextOf, ageWeeks } from './profile'
import { ageMonths, lmsAt, weightAtZ, zScore } from './who-weight'
import { corridor } from './who-weight'
import type { WeightPoint } from './weight'
import { DEFAULT_DAY, isNight, type DaySettings } from './day'
import { t } from '../i18n'
import { formatCorridorKg, formatGrams, formatHours, formatSpan } from '../i18n/format'

/**
 * Напоминания, которые сервер только доставляет: когда и что сказать, решает
 * приложение — у него есть возраст, нормы, настройки дня и записи веса, а у сервера
 * без аккаунта нет даже даты рождения. Список уезжает вместе с расписанием и каждый
 * раз заменяет предыдущий, поэтому считать его нужно из текущего состояния целиком.
 *
 * Решения заказчика (28.09.2026): «пора вставать» — только с 4 месяцев, только если
 * сон отмечен, выключается в настройках; вечернее — на последнее бодрствование перед
 * укладыванием; ориентир по весу — раз в месяц; ночью пушей нет.
 */

export type ReminderKind = 'wake_day' | 'wake_evening' | 'weight_hint'

export interface Reminder {
  kind: ReminderKind
  at: number
  title: string
  body: string
}

export interface ReminderInput {
  profile: ChildProfile
  program: WakeProgram
  settings: DaySettings
  now: number
  /** Малыш спит с этого момента — ответ «да, уснул». `null` — не спит или неизвестно. */
  asleepAt: number | null
  /** Точки веса, включая вес при рождении. */
  weights: WeightPoint[]
}

const МИН = 60_000
const DAY = 24 * 3600 * 1000

const время = (millis: number): string => {
  const d = new Date(millis)
  return `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`
}

/** Момент «сегодня в N минут от полуночи» для того же дня, что [millis]. */
function вДень(millis: number, minutesOfDay: number): number {
  const d = new Date(millis)
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, minutesOfDay).getTime()
}

/** Ближайшая «месячная годовщина» рождения в 10:00 строго после [now]; до двух лет. */
export function nextWeightHintAt(birthMillis: number, now: number): number | null {
  const birth = new Date(birthMillis)
  for (let k = 1; k <= 24; k += 1) {
    const at = new Date(birth.getFullYear(), birth.getMonth() + k, birth.getDate(), 10, 0).getTime()
    if (at > now) return at
  }
  return null
}

function ориентирПоВесу(input: ReminderInput): Reminder | null {
  const { profile, weights, now } = input
  const at = nextWeightHintAt(profile.birthMillis, now)
  if (at === null) return null
  const имя = profile.name
  const возраст = ageTextOf(profile.birthMillis, at)
  const birth = weights.find((p) => p.birth === true)
  const взвешивания = weights.filter((p) => p.birth !== true)
  const last = взвешивания[взвешивания.length - 1]

  // Коридор к дате пуша: общий по ВОЗ или «его» — вокруг центиля при рождении
  let коридорТекст: string
  if (birth !== undefined) {
    const z = zScore(birth.grams, lmsAt(profile.gender, 0))
    const lms = lmsAt(profile.gender, ageMonths(profile.birthMillis, at))
    const диапазон = formatCorridorKg(weightAtZ(z - 0.67, lms), weightAtZ(z + 0.67, lms))
    коридорТекст = t(profile.gender === 'GIRL' ? 'reminder_weight_born_girl' : 'reminder_weight_born_boy', formatGrams(birth.grams), диапазон)
  } else {
    const c = corridor(profile.gender, profile.birthMillis, at)
    коридорТекст = t('reminder_weight_usual', formatCorridorKg(c.low, c.high))
  }

  const body =
    last === undefined
      ? t('reminder_weight_first', имя, возраст, коридорТекст)
      : t('reminder_weight_again', имя, возраст, formatGrams(last.grams), formatSpan(Math.round((at - last.at) / DAY)), коридорТекст)
  return { kind: 'weight_hint', at, title: t('reminder_weight_title', имя), body }
}

function подъёмы(input: ReminderInput): Reminder[] {
  const { profile, program, settings, now, asleepAt } = input
  if (asleepAt === null || !settings.wakePush) return []
  const sleep = sleepNormOf(program)
  if (sleep.wakePushFromWeeks === undefined || ageWeeks(profile, now) < sleep.wakePushFromWeeks) return []

  const napMax = sleep.napMinutes[1]
  const день = asleepAt + napMax * МИН
  const вечер = вДень(asleepAt, settings.bedtimeMin - program.windowMin[1])
  const дневное: Reminder = {
    kind: 'wake_day',
    at: день,
    title: t('reminder_wake_title'),
    body: t('reminder_wake_body', profile.name, formatHours(napMax)),
  }
  const вечернее: Reminder = {
    kind: 'wake_evening',
    at: вечер,
    title: t('reminder_evening_title'),
    body: t('reminder_evening_body', время(вДень(asleepAt, settings.bedtimeMin))),
  }
  // Из двух остаётся то, что раньше: второе разбудило бы уже проснувшегося
  const годные = [дневное, вечернее].filter((r) => r.at > now && r.at > asleepAt).sort((a, b) => a.at - b.at)
  const out: Reminder[] = годные.length === 0 ? [] : [годные[0]!]
  return out
}

/** Все напоминания из текущего состояния. Ночью ничего не приходит — такие отбрасываются. */
export function planReminders(input: ReminderInput): Reminder[] {
  const settings = input.settings ?? DEFAULT_DAY
  const все = [...подъёмы({ ...input, settings })]
  const вес = ориентирПоВесу(input)
  if (вес !== null) все.push(вес)
  return все.filter((r) => !isNight(r.at, settings)).sort((a, b) => a.at - b.at)
}
